import { Component, computed, DestroyRef, effect, inject, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FullWindowService } from '@modules/common/services/full-window.service';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { FormsPanelNavigationService } from '@modules/forms/services/forms-panel-navigation.service';
import { HostedNotificationViewService } from '@modules/desktop-notifications/hosted-notification-view.service';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { SheetsAppClientService } from '@modules/sheets/app-client/sheets-app-client.service';
import { SplitViewComponent } from '@modules/split-view/components/split-view/split-view.component';
import { RoutedChatLayout } from '@modules/common/types/routed-chat-layout';
import { LayoutCompanionPresentationService } from './layout-companion-presentation.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.less', '../../styles.less'],
  providers: [
    DynamicPanelService,
    FormsPanelNavigationService,
    KnowledgePanelService,
    LayoutCompanionPresentationService,
    {
      provide: APPLICATION_SCOPE_TOKEN,
      useValue: ApplicationScope.MAIN,
    },
  ],
  standalone: false,
})
export class LayoutComponent {
  protected readonly companionPresentation = inject(LayoutCompanionPresentationService);
  protected readonly fullWindow = inject(FullWindowService);
  private readonly routedChatOwners = new WeakMap<RoutedChatLayout, symbol>();
  /** The Dynamic Panel's split: the only split view in this template. */
  private readonly dpSplit = viewChild(SplitViewComponent);

  /** RouterOutlet activates only after navigation is accepted; cancelled navigation changes no lease. */
  protected routedLayoutActivated(component: unknown): void {
    if (!(component instanceof RoutedChatLayout)) return;
    const owner = Symbol('routed Chat layout');
    this.routedChatOwners.set(component, owner);
    this.companionPresentation.registerRoutedChat(owner, component.standaloneCompact);
  }

  protected routedLayoutDeactivated(component: unknown): void {
    if (!(component instanceof RoutedChatLayout)) return;
    const owner = this.routedChatOwners.get(component);
    if (owner === undefined) return;
    this.routedChatOwners.delete(component);
    this.companionPresentation.releaseRoutedChat(owner);
  }

  public constructor() {
    const notificationPresentation = inject(HostedNotificationViewService).mountPresentation(
      'dp',
      computed(() => !this.companionPresentation.standalone() && this.companionPresentation.replyTemplate() === null),
    );
    inject(DestroyRef).onDestroy(() => notificationPresentation.close());
    // Applied native requests come after their existing dirty guard; background refresh/deletion emits nothing.
    inject(DynamicPanelService)
      .onNativeOpenAccepted()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.companionPresentation.closeReply());
    // The MCP contract's (6 Oct) "open a workbook" reaches this tab whatever app it shows: the signed-in shell
    // reports it as one of the user's clients and listens, outside the lazily loaded editor.
    inject(SheetsAppClientService).start();
    // SBUX-1144/1145: when a full-window session ends, the Dynamic Panel opens and is saved open, whatever it was.
    let fullWindowActive = false;
    effect(() => {
      const active = this.fullWindow.activeApp() !== null;
      if (fullWindowActive && !active) untracked(() => this.dpSplit()?.open());
      fullWindowActive = active;
    });
  }
}
